<template>
    <div class="availability-view container-fluid">
        <div class="row mb-3">
            <div class="col-md-9">
                <h1 class="page-title">
                    <SidebarButton :dashboardUrl="dashboardUrl" />
                    {{ componentTitle }}
                </h1>
            </div>
            <div class="col-md-3">
                <button class="btn btn-primary btn-block" v-if="shouldDisplayCreateButton" v-on:click="handleCreate">Nova disponibilidade</button>
            </div>
        </div>
        <div class="row mt-2">
            <div class="col">
                <ProductFilter 
                    v-if="shouldDisplayProductFilter"
                    @onSelectedProducts="handleSelectedProducts"
                    :products='products'
                    :loading="loadingProducts"
                ></ProductFilter>
            </div>
        </div>
        <div class="calendar-loading" v-if="loading">
            <i class="material-icons loading-icon-size">cached</i>
        </div>
        <div class="row">
            <div class="col">
                <AvailabilityForm
                    v-if="managingAvailability"
                    :products="products"
                    :availability="currentAvailability"
                    :validation="validation"
                    :store-availability-url="storeAvailabilityUrl"
                    :on-submit="handleAvailabilitySubmit"
                    :on-cancel="closeForm"
                    :on-delete="handleAvailabilityDelete"
                    :default-begin-date="availabilityFormDefaultBeginDate"
                    :default-end-date="availabilityFormDefaultEndDate"
                    :app-minute-set-interval="appMinuteSetInterval"
                ></AvailabilityForm>
                <CalendarView
                    v-else
                    :initial-month="currentMonth"
                    :reference-date="now"
                    :on-month-change="handleMonthChange"
                    :render-date="renderDate"
                ></CalendarView>
            </div>
        </div>
    </div>
</template>

<script>
import { requestJson, requestAllPagesJson, MAX_MINUTE_DAY, minToHour } from '../helpers';
import AvailabilityForm from './AvailabilityForm';
import CalendarView from './CalendarView';
import ProductFilter from './ProductFilter.vue';
import DisplayCalendar from './mixins/DisplayCalendar';
import SidebarButton from './SidebarButton';

export default {
    mixins: [
        DisplayCalendar
    ],
    components: {
        AvailabilityForm,
        ProductFilter,
        CalendarView,
        SidebarButton,
    },
    props: {
        availableProductsUrl: String,
        storeAvailabilityUrl: String,
        availabilityDatesUrl: String,
        componentTitle: String,
        dashboardUrl: String,
        appMinuteSetInterval: String,
    },
    data(){
        return {
            products: [],
            formEnabled: false,
            currentAvailability: null,
            validation: null,
            loading: false,
            chosenProducts: [],
            loadingProducts: false
        };
    },
    computed: {
        managingAvailability(){
            return this.formEnabled;
        },
        shouldDisplayCreateButton(){
            return !this.formEnabled;
        },
        availabilityFormDefaultBeginDate(){
            return this.currentInterval ? this.currentInterval[0] : null;
        },
        availabilityFormDefaultEndDate(){
            return this.currentInterval ? this.currentInterval[1] : null;
        },
        shouldDisplayProductFilter(){
            return !this.formEnabled;
        }
    },
    methods: {
        handleCreate(){
            this.openForm();
        },
        openForm(availability){
            this.formEnabled = true;
            this.currentAvailability = availability || null;
        },
        closeForm(){
            this.formEnabled = false;
            this.currentAvailability = null;
            this.validation = null;
        },
        async requestMonthData(){
            if (this.currentInterval == null) {
                return;
            }

            const [
                { dateString: from },
                { dateString: to }
            ] = this.currentInterval;

            const params = { from, to, id_products: this.chosenProducts.map(p => p.id).join(',') };

            this.loading = true;

            let arr;
            try {
                arr = await requestJson(this.availabilityDatesUrl, params);
            } finally {
                this.loading = false;
            }

            if (arr) {
                this.monthlyIndex = arr.reduce((carry, av) => ({
                    ...carry,
                    [av.applies_at]: [ ...(carry[av.applies_at] ?? []), av ]
                }), {});
            }
        },

        renderDate(date, createElement){
            const availabilityMap = this.monthlyIndex[date.dateString];
            const availabilities = availabilityMap ? Object.keys(availabilityMap).map(key => availabilityMap[key]) : [];

            const lis = availabilities.map(availability => {
                const title = this.formatAvailability(availability);

                const isTariffGroupDeleted = availability.tariffs?.some(tariff=>tariff.group?.deleted_at);

                return createElement('li', [
                    createElement('a', {
                        domProps: { title, href: 'javascript:void(0)' },
                        on: { click: () => this.openForm(availability) },
                        'class': 'availability ' + (availability.date_occupation_quantity !== null && availability.date_occupation_quantity >= availability.quantity ? 'full' : ''),
                    }, [
                        createElement('span', title),
                        ...(isTariffGroupDeleted ? [createElement('i', { domProps: {title: 'Esta disponibilidade está atrelada a um grupo de tarifas arquivado. Selecione outro grupo de tarifas'}, 'class': 'material-icons' }, 'warning')] : [])
                    ])
                ]);
            });

            return createElement('ul', lis);
        },
        formatAvailability(av){
            const labels = {
                per_app: 'Geral',
                per_products: 'Produto',
                per_tariffs: 'Tarifa',
                per_product_tariff_types: 'Tipos de Tarifa',
                per_additionals: 'Adicional'
            };

            const type = labels[av.type];
            const name = this.availabilityName(av);

            const data = [`${type} - ${name}`];

            if (av.from_minutes !== 0 || av.to_minutes !== MAX_MINUTE_DAY) {
                data.push(`[${minToHour(av.from_minutes)} - ${minToHour(av.to_minutes)}]`);
            }

            const quantityStr = [av.date_occupation_quantity, av.quantity].filter(item => item != null).join('/');
            data.push(`(${quantityStr})`);

            return data.join(' ');
        },
        availabilityName(availability){
            switch(availability.type){
                case 'per_products':
                    return availability.products.map(p => p.name).join(', ');
                case 'per_tariffs':
                    return availability.tariffs.map(t => t.formatted_name).join(', ');
                case 'per_product_tariff_types':
                    return availability.tariff_types.map(tt => tt.name).join(', ');
                case 'per_additionals':
                    return availability.additionals.map(a => a.title).join(', ');
                default:
                    return '';
            }
        },

        handleAvailabilitySubmit(body){
            const shouldUpdate = this.currentAvailability != null;
            const url = shouldUpdate ? this.currentAvailability.update_url : this.storeAvailabilityUrl;

            if(shouldUpdate){
                body.append('_method', 'PATCH');
            }

            requestJson(url, body, 'POST')
                .then(json => {
                    if(json.status == 'OK')
                        this.closeForm();
                    else
                        this.validation = json.errors;
                });
        },

        handleAvailabilityDelete(all = false){
            const params = { delete_clones: !!all };

            requestJson(this.currentAvailability.delete_url, params, 'DELETE')
                .then(json => {
                    if(json.status == 'OK')
                        this.closeForm();
                });
        },

        handleSelectedProducts(products){
            this.chosenProducts = [...products]
            this.requestMonthData();
        },
        async getAllProducts(){
            this.loadingProducts = true
            
            const products = await requestAllPagesJson(this.availableProductsUrl);
            this.products = products.filter(prod => prod.active);

            this.loadingProducts = false;
            
            return this.products;
        }
    },    
    async mounted(){
        this.getAllProducts()
    }
}
</script>

<style lang="scss">
.availability-view {
    .date-anchor {
        ul {
            margin: 0;
            padding: 0;
            list-style: none;
        }

        li {
            display: flex;
            margin: .5rem 0 0;
        }

        .availability {
            display: flex;
            width: 100%;
            padding: 3px 5px;
            font-size: .7rem;
            border-radius: 5px;
            text-align: left;
            text-decoration: none;
            background-color: #131552;
            color: #fff;

            i {
                font-size: 1.05rem;
            }

            &:hover {
                span {
                    text-decoration: underline;
                }
            }

            &.full {
                background-color: #d9463c;
            }
        }
    }
}
</style>
